import { useEffect, useMemo, useRef } from "react";
import { useLocation } from "react-router-dom";

import recordVisit, {
	isModuleRootRedirect,
	loadRecentDestinations,
	nowKey,
	RECENT_DESTINATIONS_KEY
} from "~/helpers/recentDestinations";
import { useAppSelector } from "~/hooks/reduxHooks";
import { getFeaturePageMapping, getFlatPageMapping, getPageInfo, getSectionIcon, getSectionKey } from "~/locationMaps";

const DWELL_BEFORE_RECORD_MS = 20_000;
const RESTAMP_INTERVAL_MS = 300_000;

const useRecentDestinations = (): void => {
	const { pathname } = useLocation();
	const application = useAppSelector((state) => state.layout.application);
	const previousPathname = useRef<string | null>(null);

	const flatPageMapping = useMemo(() => getFlatPageMapping(application), [application]);
	const featurePageMapping = useMemo(() => getFeaturePageMapping(application), [application]);

	useEffect(() => {
		const cameFrom = previousPathname.current;
		previousPathname.current = pathname;
		if (pathname === "/" || pathname.includes("/import") || pathname.endsWith("/new")) return;
		const segments = pathname.split("/").filter(Boolean);
		if (segments.length < 2) return;
		if (isModuleRootRedirect(cameFrom, segments[0])) return;
		const pageInfo = getPageInfo(pathname, flatPageMapping);
		if (!pageInfo?.label) return;
		const record = () => {
			const next = recordVisit(
				loadRecentDestinations(),
				{
					icon: getSectionIcon(pathname, featurePageMapping) ?? pageInfo.icon,
					label: pageInfo.label,
					labelKey: pageInfo.labelKey,
					module: segments[0],
					path: pathname,
					section: getSectionKey(pathname, flatPageMapping)
				},
				nowKey()
			);
			localStorage.setItem(RECENT_DESTINATIONS_KEY, JSON.stringify(next));
		};
		const restampWhileVisible = () => {
			if (document.visibilityState === "visible") record();
		};
		let interval: number | undefined;
		const timer = window.setTimeout(() => {
			record();
			interval = window.setInterval(restampWhileVisible, RESTAMP_INTERVAL_MS);
		}, DWELL_BEFORE_RECORD_MS);
		return () => {
			window.clearTimeout(timer);
			if (interval !== undefined) window.clearInterval(interval);
		};
	}, [featurePageMapping, flatPageMapping, pathname]);
};

export default useRecentDestinations;
